import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Car,
  DollarSign,
  Zap,
  Edit,
  ChevronDown,
  ChevronUp,
  Building2,
  Timer,
  Gift,
  Ban,
  Loader
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import InfoTooltip from '../../components/InfoTooltip';
import { API_BASE } from '../../utils/api';

const FareConfigDetails = ({ config, onBack, onEdit }) => {
  const { hasPermission } = useAuth();
  const [expandedSections, setExpandedSections] = useState({
    vehicleTypes: false,
    surge: false,
    fees: false,
    coupons: false,
    cancellation: false,
    zones: false
  });
  const [expandedVehicleTypes, setExpandedVehicleTypes] = useState({});
  const [fareData, setFareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch fare configuration data
  useEffect(() => {
    const fetchFareConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If config is passed as prop, use it directly
        if (config && config._id) {
          setFareData(config);
          setLoading(false);
          return;
        }

        // Otherwise fetch from API
        const response = await fetch(`${API_BASE}/fare/config`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setFareData(data);
      } catch (error) {
        console.error('Error fetching fare configuration:', error);
        setError(error.message || 'Failed to fetch fare configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchFareConfig();
  }, [config]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleVehicleType = (vehicleType) => {
    setExpandedVehicleTypes(prev => ({
      ...prev,
      [vehicleType]: !prev[vehicleType]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: fareData?.currency || 'INR'
    }).format(amount);
  };

  const formatFeeValue = (fee) => {
    if (fee.type === 'percentage') {
      return `${fee.value}%`;
    }
    return formatCurrency(fee.value);
  };

  const renderVehicleTypeCard = (vehicleType, config) => {
    const isExpanded = expandedVehicleTypes[vehicleType];
    
    return (
      <div key={vehicleType} className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => toggleVehicleType(vehicleType)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Car className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{vehicleType.replace('_', ' ')}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              config.baseFare > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {config.baseFare > 0 ? 'Base Fare Model' : 'Range Pricing Model'}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm text-gray-500">Min - Max Fare</p>
              <p className="text-sm font-semibold text-gray-900">
                {formatCurrency(config.minFare)} - {formatCurrency(config.maxFare)}
              </p>
            </div>
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Base Fare
                  <InfoTooltip text="Initial fare charged when a trip starts" />
                </p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(config.baseFare)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Min Fare
                  <InfoTooltip text="Minimum fare for any trip" />
                </p>
                <p className="text-lg font-semibold text-green-600">{formatCurrency(config.minFare)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Max Fare
                  <InfoTooltip text="Maximum fare cap for any trip" />
                </p>
                <p className="text-lg font-semibold text-red-600">{formatCurrency(config.maxFare)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Wait Time Cost
                  <InfoTooltip text="Cost per minute when vehicle is waiting" />
                </p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(config.waitTimeCostPerMin)}/min</p>
              </div>
            </div>

            {config.rangePricing && config.rangePricing.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Distance-based Pricing (First 5 tiers)</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Distance Range (km)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fare</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {config.rangePricing.slice(0, 5).map((range, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {range.minDistance} - {range.maxDistance || '∞'} km
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">
                            {formatCurrency(range.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {config.rangePricing.length > 5 && (
                    <p className="text-xs text-gray-500 mt-2">
                      ... and {config.rangePricing.length - 5} more pricing tiers
                    </p>
                  )}
                </div>
              </div>
            )}

            {config.feeWithTax && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Fees & Taxes</h4>
                {Object.entries(config.feeWithTax).map(([feeType, feeConfig]) => (
                  <div key={feeType} className="bg-gray-50 rounded-md p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {feeType.replace(/([A-Z])/g, ' $1').toLowerCase()}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {formatFeeValue(feeConfig)}
                      </span>
                    </div>
                    {feeConfig.tax && (
                      <div className="space-y-1">
                        {Object.entries(feeConfig.tax).map(([taxType, taxConfig]) => (
                          <div key={taxType} className="flex justify-between text-xs text-gray-600">
                            <span className="uppercase">{taxType}</span>
                            <span>{formatFeeValue(taxConfig)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSurgeMultipliers = () => (
    <div className="space-y-4">
      {fareData?.surge?.multipliers?.map((multiplier, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              <span className="font-medium text-gray-900">
                {multiplier.type === 'multiplier' ? `${multiplier.value}x Multiplier` : `+${formatCurrency(multiplier.value)} Fixed`}
              </span>
            </div>
            <span className="text-sm text-gray-500">
              {multiplier.timeRange.start} - {multiplier.timeRange.end}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Days</p>
              <p className="text-gray-900">{multiplier.days.join(', ')}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">Zones</p>
              <p className="text-gray-900">{multiplier.zones.join(', ')}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCouponRules = () => (
    <div className="space-y-4">
      {fareData?.dynamicCouponRules?.map((coupon, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Gift className="h-5 w-5 text-green-500" />
              <span className="font-bold text-gray-900">{coupon.code}</span>
            </div>
            <span className="text-sm font-semibold text-green-600">
              {coupon.type === 'percentage' ? `${coupon.value}% OFF` : formatCurrency(coupon.value)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-3">{coupon.description}</p>
          <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
            <div>Max Discount: {formatCurrency(coupon.maxDiscount)}</div>
            <div>Min Fare: {formatCurrency(coupon.minFare)}</div>
            <div>Valid From: {coupon.validFrom}</div>
            <div>Valid To: {coupon.validTo}</div>
          </div>
        </div>
      ))}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading fare configuration...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Configuration</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={onBack}
          className="btn btn-secondary"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  // No data state
  if (!fareData) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-500 mb-4">No fare configuration data available.</p>
        <button
          onClick={onBack}
          className="btn btn-secondary"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="btn-icon"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="page-title">Fare Configuration Details</h1>
                <p className="page-subtitle">
                  Region: {fareData.regionCode} • Currency: {fareData.currency}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                fareData.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {fareData.isActive ? 'Active' : 'Inactive'}
              </span>
              {hasPermission('fare', 'write') && (
                <button 
                  onClick={onEdit}
                  className="btn btn-primary"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Configuration
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <Car className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-sm text-gray-500">
                Vehicle Types
                <InfoTooltip text="Number of vehicle types configured" />
              </p>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(fareData?.vehicleTypes || {}).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <Zap className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-500">
                Surge Rules
                <InfoTooltip text="Number of surge pricing rules active" />
              </p>
              <p className="text-2xl font-bold text-gray-900">{fareData?.surge?.multipliers?.length || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <Gift className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-500">
                Coupon Rules
                <InfoTooltip text="Number of coupon rules configured" />
              </p>
              <p className="text-2xl font-bold text-gray-900">{fareData?.dynamicCouponRules?.length || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <Timer className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-sm text-gray-500">
                Accept Duration
                <InfoTooltip text="Time driver has to accept trip request" />
              </p>
              <p className="text-2xl font-bold text-gray-900">{fareData?.tripAcceptDuration}s</p>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Types Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('vehicleTypes')}
          className="w-full px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <Car className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Vehicle Types & Pricing</h2>
          </div>
          {expandedSections.vehicleTypes ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>
        {expandedSections.vehicleTypes && (
          <div className="px-6 pb-6 space-y-6">
            {Object.entries(fareData?.vehicleTypes || {}).map(([vehicleType, config]) =>
              renderVehicleTypeCard(vehicleType, config)
            )}
          </div>
        )}
      </div>

      {/* Surge Pricing Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('surge')}
          className="w-full px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <Zap className="h-5 w-5 text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-900">Surge Pricing</h2>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              fareData?.surge?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {fareData?.surge?.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          {expandedSections.surge ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>
        {expandedSections.surge && (
          <div className="px-6 pb-6">
            {renderSurgeMultipliers()}
          </div>
        )}
      </div>

      {/* Fees & Taxes Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('fees')}
          className="w-full px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <DollarSign className="h-5 w-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900">Fees & Taxes</h2>
          </div>
          {expandedSections.fees ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>
        {expandedSections.fees && (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Platform Fees */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Platform Fees</h3>
              <div className="space-y-2">
                {Object.entries(fareData?.fees || {}).map(([feeType, feeConfig]) => (
                  <div key={feeType} className="flex justify-between text-sm">
                    <span className="text-gray-600 capitalize">{feeType.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                    <span className="font-medium text-gray-900">{formatFeeValue(feeConfig)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic Fees */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Dynamic Fees</h3>
              <div className="space-y-2">
                {Object.entries(fareData?.dynamicFees || {}).map(([feeType, feeConfig]) => (
                  <div key={feeType} className="flex justify-between text-sm">
                    <span className="text-gray-600 capitalize">{feeType.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                    <span className="font-medium text-gray-900">{formatFeeValue(feeConfig)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic Taxes */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Dynamic Taxes</h3>
              <div className="space-y-2">
                {Object.entries(fareData?.dynamicTaxes || {}).map(([taxType, taxConfig]) => (
                  <div key={taxType} className="flex justify-between text-sm">
                    <span className="text-gray-600 capitalize">{taxType.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                    <span className="font-medium text-gray-900">{formatFeeValue(taxConfig)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Coupon Rules Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('coupons')}
          className="w-full px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <Gift className="h-5 w-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900">Coupon Rules</h2>
          </div>
          {expandedSections.coupons ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>
        {expandedSections.coupons && (
          <div className="px-6 pb-6">
            {renderCouponRules()}
          </div>
        )}
      </div>

      {/* Cancellation Policy Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('cancellation')}
          className="w-full px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <Ban className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">Cancellation Policy</h2>
          </div>
          {expandedSections.cancellation ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>
        {expandedSections.cancellation && (
          <div className="px-6 pb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="mb-4">
                <h3 className="font-medium text-gray-900 mb-2">Free Cancellation Window</h3>
                <p className="text-sm text-gray-600">
                  {fareData?.cancellationPolicy?.freeCancellationWindow} minutes after booking
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Cancellation Fees</h3>
                <div className="space-y-2">
                  {fareData?.cancellationPolicy?.cancellationFees?.map((fee, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {fee.timeWindow ? `Within ${fee.timeWindow} minutes` : 'After all time windows'}
                      </span>
                      <span className="font-medium text-gray-900">
                        {fee.fee === 0 ? 'Free' : formatCurrency(fee.fee)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Zones Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <button
          onClick={() => toggleSection('zones')}
          className="w-full px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <Building2 className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">Zone Configuration</h2>
          </div>
          {expandedSections.zones ? 
            <ChevronUp className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>
        {expandedSections.zones && (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(fareData?.zones || {}).map(([zoneName, zoneConfig]) => (
              <div key={zoneName} className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-medium text-gray-900 mb-3 capitalize">{zoneName}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium text-gray-900 capitalize">{zoneConfig.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Value</span>
                    <span className="font-medium text-gray-900">
                      {zoneConfig.type === 'multiplier' ? `${zoneConfig.value}x` : formatCurrency(zoneConfig.value)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Fare</span>
                    <span className="font-medium text-gray-900">{formatCurrency(zoneConfig.baseFare)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Additional Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              Due Repay Cycle
              <InfoTooltip text="Payment cycle schedule for drivers" />
            </h3>
            <p className="text-sm text-gray-600">
              Every {fareData?.dueRepayCycle?.value} {fareData?.dueRepayCycle?.type}(s)
            </p>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              Trip Accept Duration
              <InfoTooltip text="Maximum time for driver to accept trip" />
            </h3>
            <p className="text-sm text-gray-600">
              {fareData?.tripAcceptDuration} seconds
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FareConfigDetails;